/**
 * @file marketDataApi.ts
 * @description RTK Query API slice for market data coverage and OHLCV candle queries.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import type { TradingSymbol, Timeframe } from '@forex-builder/shared-types';
import type { RootState } from '../../../app/store.js';
import { createBaseQueryWithReauth } from '../../../shared/api/baseQueryWithReauth.js';

export interface DataCoverageItem {
  id: string;
  symbol: TradingSymbol;
  timeframe: Timeframe;
  availableFrom: string;
  availableTo: string;
  totalCandles: number;
  lastSyncAt: string;
  status: 'active' | 'syncing' | 'error';
}

export interface OHLCVBarClient {
  symbol: TradingSymbol;
  timeframe: Timeframe;
  time: string; // ISO 8601 string
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  spreadAvg: number;
}

export interface GetCandlesResponse {
  success: boolean;
  data: {
    symbol: TradingSymbol;
    timeframe: Timeframe;
    count: number;
    candles: OHLCVBarClient[];
  };
}

export interface GetSymbolsResponse {
  success: boolean;
  data: {
    coverage: DataCoverageItem[];
  };
}

export const marketDataApi = createApi({
  reducerPath: 'marketDataApi',
  baseQuery: createBaseQueryWithReauth('/api/v1/data'),
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getSymbols: builder.query<GetSymbolsResponse, void>({
      query: () => '/symbols',
    }),
    getCandles: builder.query<GetCandlesResponse, { symbol: TradingSymbol; timeframe: Timeframe; limit?: number }>({
      query: ({ symbol, timeframe, limit = 1000 }) => `/candles?symbol=${symbol}&timeframe=${timeframe}&limit=${limit}`,
    }),
  }),
});

export const { useGetSymbolsQuery, useGetCandlesQuery } = marketDataApi;
